/**
 * Central plan-access configuration.
 * Source of truth for what each plan can access — derived from FeatureComparison.js matrix.
 *
 * Plan hierarchy: starter < professional < partner < agent < consultant
 * During trial: user gets the plan they signed up for (or 'professional' if no plan set)
 */

const PLAN_RANK = { starter: 1, professional: 2, partner: 3, agent: 4, consultant: 5 };

/**
 * Returns the effective plan for a user, accounting for trial state and system owner.
 */
export function getEffectivePlan(user) {
  if (!user) return null;
  if (user.email === 'podracer500@hotmail.com') return 'consultant'; // owner gets max
  const plan = user.subscription_plan;
  const status = user.subscription_status;
  // Active subscription with a known plan
  if (plan && PLAN_RANK[plan]) return plan;
  // Trialing without a specific plan → match workspace type
  if (status === 'trialing') {
    return user.workspace_type === 'partner' ? 'partner' : 'professional';
  }
  // Fallback
  return 'starter';
}

/**
 * Check if user's effective plan meets the minimum required plan.
 */
export function hasPlanAccess(user, minPlan) {
  if (!minPlan) return true; // no restriction
  const effective = getEffectivePlan(user);
  if (!effective) return false;
  return (PLAN_RANK[effective] || 0) >= (PLAN_RANK[minPlan] || 0);
}

/**
 * Sidebar item plan requirements.
 * null = available to all plans.
 */
export const SIDEBAR_PLAN_GATES = {
  '/courses':                        null,
  '/dashboard':                      null,
  '/generate':                       null,
  '/templates':                      null,
  '/learners':                       null,
  '/departments':                    null,
  '/roles':                          null,
  '/compliance':                     null,
  '/compliance/risk-radar':          null,
  '/compliance/regulatory-intelligence': 'professional',
  '/playbooks':                      'professional',
  '/settings/organisation':          null,
  '/team':                           'professional',
  '/settings/branding':              'professional',
  '/settings/ai':                    null,
  '/billing':                        null,
  '/partner':                        'partner',
  '/clients/compliance':             'partner',
  '/admin/chat-logs':                null, // gated by requireSystemOwner already
  '/admin/users':                    null, // gated by requireSystemOwner already
};

export { PLAN_RANK };
